<template>
  <div class="visitor-login">
    <div class="header">
      <h1 class="header-title">员工登录</h1>
      <!-- <p class="header-subtitle">输入手机号获取验证码（10分钟有效）</p> -->
    </div>

    <div v-if="featureLoading" class="content">
      <van-loading type="spinner" size="24px" style="display:block;margin:48px auto;" />
    </div>

    <div v-else-if="!featureEnabled" class="content">
      <van-empty :description="featureDisabledText" />
    </div>

    <div v-else class="content">
      <div class="form-item">
        <label class="form-label"><span class="required">*</span>手机号</label>
        <input
          v-model.trim="phone"
          class="form-input"
          type="tel"
          placeholder="请输入手机号"
          maxlength="11"
          inputmode="numeric"
        />
      </div>

      <div class="form-item code-row">
        <div class="code-left">
          <label class="form-label"><span class="required">*</span>验证码</label>
          <input
            v-model.trim="code"
            class="form-input"
            type="text"
            placeholder="请输入验证码"
            maxlength="6"
          />
        </div>

        <van-button
          class="send-btn"
          type="info"
          :disabled="countdown > 0 || sending"
          @click="onSendCode"
        >
          {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
        </van-button>
      </div>

      <van-button
        type="info"
        block
        class="login-btn"
        :loading="logging"
        loading-text="登录中…"
        :disabled="!canLogin"
        @click="onLogin"
      >
        登录
      </van-button>

      <p class="tip">登录成功后将进入访客审批页面。</p>
    </div>
  </div>
</template>

<script>
import * as common from '@/api/common'
import { IotService } from '@/api/iotService'

const EMPLOYEE_TOKEN_KEY = 'employeeSceneAccessToken'
const EMPLOYEE_TOKEN_EXPIRE_AT_KEY = 'employeeSceneAccessToken_expireAt'
const TTL_MINUTES = 60

export default {
  name: 'EmployeeLogin',
  data () {
    return {
      sid: '',
      redirectTo: '/visitor/audit',
      featureEnabled: true,
      featureLoading: false,
      featureDisabledText: '该车场暂未开启访客功能，无法进入员工审批',

      phone: '',
      code: '',
      sending: false,
      logging: false,

      countdown: 0,
      timer: null
    }
  },
  computed: {
    canLogin () {
      return this.phone && this.code && !this.logging
    }
  },
  created () {
    this.sid = (this.$route && this.$route.query && this.$route.query.sid) ? String(this.$route.query.sid) : ''
    this.redirectTo = (this.$route && this.$route.query && this.$route.query.redirect)
      ? String(this.$route.query.redirect)
      : `/visitor/audit?sid=${encodeURIComponent(this.sid)}`
    this.checkVisitorFeature()
  },
  beforeDestroy () {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  },
  methods: {
    async checkVisitorFeature () {
      if (!this.sid) {
        this.featureEnabled = false
        this.featureDisabledText = '缺少车场参数 sid'
        return
      }
      this.featureLoading = true
      try {
        const data = await IotService.getVisitorFeatureBySid(this.sid)
        this.featureEnabled = IotService.isVisitorFeatureEnabled(data)
        if (!this.featureEnabled) {
          this.$router.replace({
            path: '/visitor/unavailable',
            query: { sid: this.sid, type: 'feature' }
          })
        }
      } catch (e) {
        this.featureEnabled = false
        this.featureDisabledText = '访客功能状态获取失败，请稍后重试'
        this.$toast(this.featureDisabledText)
      } finally {
        this.featureLoading = false
      }
    },
    startCountdown (seconds) {
      this.countdown = seconds
      if (this.timer) clearInterval(this.timer)

      this.timer = setInterval(() => {
        if (this.countdown <= 1) {
          this.countdown = 0
          clearInterval(this.timer)
          this.timer = null
          return
        }
        this.countdown -= 1
      }, 1000)
    },

    async onSendCode () {
      if (this.featureLoading || !this.featureEnabled) return
      const phoneReg = /^1\d{10}$/
      if (!phoneReg.test(this.phone)) {
        this.$toast('请输入正确的手机号')
        return
      }
      if (!this.sid) {
        this.$toast('缺少车场参数 sid')
        return
      }
      if (this.countdown > 0 || this.sending) return

      this.sending = true
      try {
        // 员工场景：use_scene=2
        const res = await IotService.getVerificationCode({
          sid: this.sid,
          account: this.phone,
          use_scene: 2
        })

        if (res && res.code === common.resCode.OK) {
          this.$toast.success('验证码已发送')
          this.startCountdown(60)
        } else {
          this.$toast(res && res.msg ? res.msg : '验证码发送失败')
        }
      } catch (e) {
        this.$toast(e && e.message ? e.message : '验证码发送失败')
      } finally {
        this.sending = false
      }
    },

    async onLogin () {
      if (this.featureLoading || !this.featureEnabled) return
      const phoneReg = /^1\d{10}$/
      if (!phoneReg.test(this.phone)) {
        this.$toast('请输入正确的手机号')
        return
      }
      if (!this.code || !this.code.trim()) {
        this.$toast('请输入验证码')
        return
      }
      if (!this.sid) {
        this.$toast('缺少车场参数 sid')
        return
      }
      if (this.logging) return

      this.logging = true
      try {
        // 员工场景：use_scene=2
        const res = await IotService.verificationCodeLogin({
          sid: this.sid,
          account: this.phone,
          use_scene: 2,
          code: this.code
        })

        if (res && res.code === common.resCode.OK && res.access_token) {
          const expireAt = Date.now() + TTL_MINUTES * 60 * 1000

          sessionStorage.setItem(EMPLOYEE_TOKEN_KEY, res.access_token)
          sessionStorage.setItem(EMPLOYEE_TOKEN_EXPIRE_AT_KEY, String(expireAt))
          localStorage.setItem(EMPLOYEE_TOKEN_KEY, res.access_token)
          localStorage.setItem(EMPLOYEE_TOKEN_EXPIRE_AT_KEY, String(expireAt))

          this.$toast.success('登录成功')
          this.$router.replace(this.redirectTo)
        } else {
          this.$toast(res && res.msg ? res.msg : '登录失败')
        }
      } catch (e) {
        this.$toast(e && e.message ? e.message : '登录失败')
      } finally {
        this.logging = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.visitor-login {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0));
}

.header {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  padding: 16px 20px;
  padding-top: calc(16px + env(safe-area-inset-top, 0));
  text-align: center;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.25);
  position: relative;
}

.header-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.header-subtitle {
  font-size: 13px;
  opacity: 0.95;
  margin: 4px 0 0 0;
}

.back-icon {
  position: absolute;
  left: 12px;
  top: calc(16px + env(safe-area-inset-top, 0));
  font-size: 20px;
  color: #fff;
}

.content {
  padding: 16px;
  max-width: 480px;
  margin: 0 auto;
}

.form-item {
  margin-bottom: 16px;
}

.code-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.code-left {
  flex: 1;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
  text-align: left;

  .required {
    color: #f56c6c;
    margin-right: 2px;
  }
}

.form-input {
  display: block;
  width: 100%;
  height: 48px;
  padding: 0 14px;
  font-size: 16px;
  color: #303133;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-sizing: border-box;

  &::placeholder {
    color: #c0c4cc;
  }

  &:focus {
    outline: none;
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
  }
}

.send-btn {
  width: 124px;
  height: 48px;
  margin-top: 0;
  flex-shrink: 0;
  border-radius: 10px;
}

.login-btn {
  height: 50px;
  font-size: 17px;
  border-radius: 10px;
  margin-top: 4px;
}

.tip {
  font-size: 12px;
  color: #909399;
  margin-top: 12px;
  line-height: 1.6;
  text-align: left;
}
</style>
